"use client";

import { useEffect, useState } from "react";
import { auth } from "@/config/firebaseApp";

const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;

interface DocumentVersion {
	name: string;
	image_path: string;
	expiration_date: string | null;
	version: number;
	verified: {
		status: "PENDING" | "VERIFIED" | "REJECTED";
		lastUpdated: { _seconds: number };
	};
	timestamps: {
		dt_create: { _seconds: number };
		dt_updated: { _seconds: number };
	};
}

const DOC_LABELS: Record<string, string> = {
	FOTO_DE_PERFIL: "Foto de perfil",
	TITULO_DE_GRADO: "Titulo de grado",
	DNI: "DNI",
	MATRICULA: "Matricula",
	CERTIFICADO_DE_ESPECIALIDAD: "Certificado de especialidad",
	SEGURO_DE_RESPONSABILIDAD_CIVIL_PROFESIONAL: "Seguro de resp. civil",
	FIRMA: "Firma",
};

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
	VERIFIED: { label: "Verificado", className: "bg-green-100 text-green-800" },
	PENDING: { label: "Pendiente", className: "bg-yellow-100 text-yellow-800" },
	REJECTED: { label: "Rechazado", className: "bg-red-100 text-red-800" },
};

function formatDate(seconds: number): string {
	return new Date(seconds * 1000).toLocaleDateString("es-AR", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
}

function formatExpiration(dateStr: string): string {
	if (dateStr.length !== 8) return dateStr;
	const y = dateStr.slice(0, 4);
	const m = dateStr.slice(4, 6);
	const d = dateStr.slice(6, 8);
	return `${d}/${m}/${y}`;
}

export default function DoctorDocumentsModal({
	uid,
	doctorName,
	onClose,
}: {
	uid: string;
	doctorName: string;
	onClose: () => void;
}) {
	const [docs, setDocs] = useState<Record<string, DocumentVersion> | null>(
		null,
	);
	/** El número de matrícula, que no es un documento sino un campo aparte. */
	const [matricula, setMatricula] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchDocs = async () => {
			try {
				const token = await auth?.currentUser?.getIdToken(true);
				if (!token) throw new Error("No autenticado");
				const res = await fetch(
					`${backendUrl}/providers/${uid}/documentation`,
					{
						headers: { Authorization: `Bearer ${token}` },
						cache: "no-store",
					},
				);
				if (!res.ok) throw new Error(`Error ${res.status}`);
				const data = await res.json();
				// La respuesta es el provider entero: los documentos vienen
				// adentro de `documentation`. Guardar el objeto completo hacía
				// que la lista quedara siempre vacía, porque ninguno de los
				// campos del provider tiene `image_path`.
				setDocs(data?.documentation ?? {});
				setMatricula(
					data?.matricula && data.matricula !== "0"
						? `${data.currentLicense?.type ?? ""} ${data.matricula}`.trim()
						: null,
				);
			} catch (err) {
				setError(
					err instanceof Error ? err.message : "Error desconocido",
				);
			} finally {
				setLoading(false);
			}
		};
		fetchDocs();
	}, [uid]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [onClose]);

	const docEntries = Object.entries(docs ?? {}).filter(
		([, doc]) => doc && doc.image_path,
	);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto mx-4">
				<div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-xl">
					<h2 className="text-base font-semibold text-gray-800">
						Documentos de {doctorName}
					</h2>
					<button
						onClick={onClose}
						className="text-gray-400 hover:text-gray-600 text-xl leading-none"
					>
						&times;
					</button>
				</div>

				<div className="p-6">
					{loading && (
						<div className="flex items-center justify-center py-12 text-gray-400">
							Cargando documentos...
						</div>
					)}

					{error && (
						<div className="flex items-center justify-center py-12 text-red-500 text-sm">
							Error al cargar documentos: {error}
						</div>
					)}

					{!loading && !error && (
						<div className="mb-4 pb-4 border-b border-gray-200 flex items-baseline gap-2">
							<span className="text-xs font-semibold text-gray-700">
								Matrícula:
							</span>
							<span className="text-sm text-gray-800">
								{matricula ?? "sin cargar"}
							</span>
						</div>
					)}

					{!loading && !error && docEntries.length === 0 && (
						<div className="flex items-center justify-center py-12 text-gray-400 text-sm">
							Sin documentos cargados
						</div>
					)}

					{!loading && !error && docEntries.length > 0 && (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{docEntries.map(([key, doc]) => {
								const status =
									STATUS_STYLES[doc.verified?.status] ??
									STATUS_STYLES.PENDING;
								const isImage =
									/\.(jpg|jpeg|png|gif|webp)$/i.test(
										doc.image_path,
									);

								return (
									<div
										key={key}
										className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2"
									>
										<div className="flex justify-between items-start">
											<span className="text-xs font-semibold text-gray-700">
												{DOC_LABELS[doc.name] ??
													doc.name}
											</span>
											<span
												className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${status.className}`}
											>
												{status.label}
											</span>
										</div>

										{isImage ? (
											<a
												href={doc.image_path}
												target="_blank"
												rel="noopener noreferrer"
											>
												{/* eslint-disable-next-line @next/next/no-img-element */}
												<img
													src={doc.image_path}
													alt={
														DOC_LABELS[doc.name] ??
														doc.name
													}
													className="w-full h-28 object-cover rounded border border-gray-100"
												/>
											</a>
										) : (
											<a
												href={doc.image_path}
												target="_blank"
												rel="noopener noreferrer"
												className="flex items-center justify-center h-28 bg-gray-50 rounded border border-gray-100 text-xs text-blue-600 hover:underline"
											>
												Ver documento (PDF)
											</a>
										)}

										<div className="text-[10px] text-gray-400 space-y-0.5">
											{doc.expiration_date && (
												<p>
													Vence:{" "}
													{formatExpiration(
														doc.expiration_date,
													)}
												</p>
											)}
											{doc.timestamps?.dt_create && (
												<p>
													Subido:{" "}
													{formatDate(
														doc.timestamps.dt_create
															._seconds,
													)}
												</p>
											)}
											<p>Version: {doc.version}</p>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
